// QMO config provider — fetches /api/qmo-settings once at app
// load and exposes it to every descendant via useQmoConfig(). The
// fetch is deferred until the provider actually mounts so anonymous
// visitors on the marketing site never pay the network cost — only
// authenticated portal routes wrap themselves in this provider.
//
// Errors are captured but never re-thrown; consumers get { config: null,
// loading: false, error } and are expected to degrade gracefully
// (fallback UI, disabled dropdowns, etc). QMO going down should never
// break the CRM.
import { createContext, useContext, useEffect, useState } from "react";
import { fetchQmoSettings } from "../lib/qmoApi";

const QmoConfigContext = createContext({
  config: null,
  loading: true,
  error: null,
});

export function QmoConfigProvider({ children }) {
  const [state, setState] = useState({ config: null, loading: true, error: null });

  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        const config = await fetchQmoSettings({ signal: ctrl.signal });
        setState({ config, loading: false, error: null });
      } catch (err) {
        if (err.name === "AbortError") return;
        setState({ config: null, loading: false, error: err });
      }
    })();
    return () => ctrl.abort();
  }, []);

  return (
    <QmoConfigContext.Provider value={state}>
      {children}
    </QmoConfigContext.Provider>
  );
}

export function useQmoConfig() {
  return useContext(QmoConfigContext);
}

// Small helper hooks so consumers don't have to defensively normalise
// the config shape every time. QMO's response can carry the lists
// under a handful of plausible keys — we pick the first non-empty one.
function _pickList(config, keys) {
  if (!config) return [];
  for (const k of keys) {
    const v = config[k];
    if (Array.isArray(v)) return v;
  }
  return [];
}

export function useQmoCategories() {
  const { config } = useQmoConfig();
  return _pickList(config, ["business_categories", "categories"]);
}

export function useQmoCities() {
  const { config } = useQmoConfig();
  return _pickList(config, ["cities", "city_configurations", "city_config"]);
}
